import React, { SetStateAction, useEffect, useState } from 'react';
import { Badge, Button, Form, Input, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DownloadOutlined, FilterOutlined, UploadOutlined } from '@ant-design/icons';

import { useLazyDownloadScheduleQuery, useSendToZupMutation } from 'app/services/edpApiV1/vacationsSchedule';
import { useAppDispatch, useAppSelector } from 'app/store';

import { uiModel } from 'entities/ui';

import { TVSSearchParams } from 'shared/types/types';
import { getErrorData } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';

import { VSUserSearchType } from 'shared/types/vacation-schedule.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { RolesUser } from 'shared/enums';
import ModalFilter from '../modalFilter/infex';
import style from './index.module.scss';

const { Search } = Input;

type TProps = {
  employeesTriger: (obj: TVSSearchParams) => void;
  filterState: { divisionId: string; filterEmployees: string; filterRequestStatuses: boolean; active: boolean };
  currentYear: number;
  currentSize: number;
  btnToSendZupStatus: boolean;
  currentSearchValue: string | undefined;
  setCurrentSearchValue: React.Dispatch<SetStateAction<string | undefined>>;
  isUploadTo1S: boolean;
  setCurrentPage: React.Dispatch<SetStateAction<number>>;
  role: string | null;
};

const Panel = ({
  filterState,
  employeesTriger,
  currentYear,
  currentSize,
  btnToSendZupStatus,
  currentSearchValue,
  setCurrentSearchValue,
  isUploadTo1S,
  setCurrentPage,
  role,
}: TProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [form] = useForm();
  const [userSearchType, setUserSearchType] = useState<VSUserSearchType>(VSUserSearchType.FIO);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const dispatch = useAppDispatch();

  const [downloadTrigger] = useLazyDownloadScheduleQuery();
  const { initialStatus } = useAppSelector(uiModel.VacationSchedule.selectors.selectVacationSchedule);

  const [sendToTrigger, resultToSendZup] = useSendToZupMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const {
    isSuccess: successSendToZup,
    isError: errorSendToZup,
    error: errorMessage,
    isLoading: loadingToZup,
    reset: resetSendToZup,
  } = resultToSendZup;

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchVal = event.target.value;
    if (searchVal === '') {
      // проверяю менялся ли список. DP-940
      dispatch(uiModel.VacationSchedule.actions.isInitialEmployeesRecord());
      // делаю запрос если список менялся или если отсутствует идентификатор инициализации списка. DP-940
      if (!initialStatus) {
        const querryParams: TVSSearchParams = {
          year: currentYear,
          size: currentSize,
          page: 1,
          filterEmployees: filterState.filterEmployees,
          filterRequestStatuses: filterState.filterRequestStatuses ? ['consideredByHR'] : undefined,
          divisionId: filterState.divisionId ? filterState.divisionId : undefined,
          search: searchVal && userSearchType === VSUserSearchType.FIO ? searchVal : undefined,
          personnelNumber: searchVal && userSearchType === VSUserSearchType.PERSONNEL_NUMBER ? searchVal : undefined,
          allAvailableOrganizations,
        };
        employeesTriger(querryParams);
        setCurrentSearchValue('');
      }
    }
  };

  const onSearch = (value: string) => {
    setCurrentPage(1);
    const querryParams: TVSSearchParams = {
      year: currentYear,
      size: currentSize,
      page: 1,
      filterEmployees: filterState.filterEmployees,
      filterRequestStatuses: filterState.filterRequestStatuses ? ['consideredByHR'] : undefined,
      divisionId: filterState.divisionId ? filterState.divisionId : undefined,
      search: value && userSearchType === VSUserSearchType.FIO ? value : undefined,
      personnelNumber: value && userSearchType === VSUserSearchType.PERSONNEL_NUMBER ? value : undefined,
      allAvailableOrganizations,
    };
    setCurrentSearchValue(value);
    employeesTriger(querryParams);
    // помечаю как получение отличных от инициализации данных
    dispatch(uiModel.VacationSchedule.actions.setInitialEmployeesStatus(false));
  };

  const uploadTo1S = () => {
    sendToTrigger(null);
    dispatch(uiModel.VacationSchedule.actions.isUploadTo1S(true));
  };

  const onDownload = () => {
    if (currentYear && currentSearchValue && filterState.divisionId !== '' && filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        search: currentSearchValue,
        divisionId: filterState.divisionId,
        filterRequestStatuses: ['consideredByHR'],
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
    if (currentYear && currentSearchValue && filterState.divisionId !== '' && !filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        search: currentSearchValue,
        divisionId: filterState.divisionId,
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
    if (currentYear && currentSearchValue && filterState.divisionId === '' && !filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        search: currentSearchValue,
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
    if (currentYear && !currentSearchValue && filterState.divisionId === '' && !filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }

    if (currentYear && !currentSearchValue && filterState.divisionId !== '' && filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        divisionId: filterState.divisionId,
        filterRequestStatuses: ['consideredByHR'],
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
    if (currentYear && !currentSearchValue && filterState.divisionId === '' && filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        filterRequestStatuses: ['consideredByHR'],
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
    if (currentYear && !currentSearchValue && filterState.divisionId !== '' && !filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        divisionId: filterState.divisionId,
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
    if (currentYear && currentSearchValue && filterState.divisionId === '' && filterState.filterRequestStatuses) {
      downloadTrigger({
        year: currentYear,
        search: currentSearchValue,
        filterRequestStatuses: ['consideredByHR'],
        filterEmployees: filterState.filterEmployees,
        allAvailableOrganizations,
      });
    }
  };

  useEffect(() => {
    if (filterState.active) {
      setShowBadge(true);
    } else {
      setShowBadge(false);
    }
  }, [filterState.active]);

  useEffect(() => {
    if (errorSendToZup) {
      dispatch(uiModel.VacationSchedule.actions.isUploadTo1S(false));
    }
  }, [errorSendToZup, dispatch]);

  useNotifications(
    successSendToZup,
    errorSendToZup,
    'Запрос на выгрузку в 1С отправлен',
    errorMessage?.message,
    resetSendToZup,
  );
  useEffect(() => {
    // нужно для удаления значения из инпута при нажатии кнопки "Очистить поиск"
    //  при случае когда поиск не дал результатов
    if (!currentSearchValue) {
      form.setFields([
        {
          name: 'search',
          value: '',
        },
      ]);
    }
  }, [currentSearchValue]);

  return (
    <>
      <div className={style.panel}>
        <div className={style.panelSearch}>
          <Form form={form}>
            <div className={style.searchBlock}>
              <Form.Item>
                <Select
                  style={{ width: 200 }}
                  value={userSearchType}
                  onChange={setUserSearchType}
                  options={Object.keys(VSUserSearchType).map(item => ({
                    value: VSUserSearchType[item],
                  }))}
                />
              </Form.Item>
              <Form.Item name="search">
                <Search
                  placeholder="Введите ФИО"
                  allowClear
                  onChange={onSearchChange}
                  onSearch={onSearch}
                  className={style.search}
                />
              </Form.Item>
            </div>
          </Form>

          <Badge dot={showBadge}>
            <Button type="primary" icon={<FilterOutlined />} onClick={() => setIsModalOpen(true)}>
              Фильтр
            </Button>
          </Badge>
        </div>
        <div className={style.panelSearch}>
          <Button type="default" icon={<DownloadOutlined />} onClick={onDownload}>
            Скачать
          </Button>
          {!allAvailableOrganizations && role === RolesUser.HRDirector && (
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={uploadTo1S}
              disabled={btnToSendZupStatus || isUploadTo1S}
              loading={loadingToZup}
            >
              Выгрузить в 1С
            </Button>
          )}
        </div>
      </div>

      <ModalFilter
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        setCurrentPage={setCurrentPage}
        emplyeesQueryTriger={employeesTriger}
        currentYear={currentYear}
        currentSize={currentSize}
        currentSearchValue={currentSearchValue}
      />
    </>
  );
};

export default Panel;
